import {
  ChannelIcon,
  DetailsPanel,
  RowCards,
  Typography,
  type RowCardMeta,
} from '@weareopenr/component-library';
import { IBrandResponse } from 'src/api/getBrands/types';
import { IChannelsAddonsResponse } from 'src/api/getChannelsAddons/types';
import { Logo } from 'src/components/Logo/Logo';
import styles from './ChannelAdoption.module.scss';

export interface ChannelAdoptionProps {
  brands: IBrandResponse[];
  addons: IChannelsAddonsResponse[];
}

interface AdoptionRow {
  addonId: string;
  addonName: string;
  count: number;
}

const buildRows = (brands: IBrandResponse[], addons: IChannelsAddonsResponse[]): AdoptionRow[] => {
  const counts = brands.reduce<Map<string, number>>((acc, brand) => {
    const uniqueAddonIds = new Set(
      brand.configured_addons.map((configured) => configured.addon_id),
    );
    uniqueAddonIds.forEach((addonId) => acc.set(addonId, (acc.get(addonId) ?? 0) + 1));
    return acc;
  }, new Map());

  return addons
    .map((addon) => ({
      addonId: addon.addon_id,
      addonName: addon.addon_name,
      count: counts.get(addon.addon_id) ?? 0,
    }))
    .sort((a, b) => b.count - a.count);
};

const adoptionMeta = (count: number): RowCardMeta =>
  count > 0
    ? {
        key: 'adoption',
        label: `Used in ${count} ${count === 1 ? 'brand' : 'brands'}`,
        colour: 'liquorice',
      }
    : { key: 'adoption', label: 'Not used', colour: 'tomato' };

export const ChannelAdoption = ({ brands, addons }: ChannelAdoptionProps) => (
  <DetailsPanel title="Channel adoption">
    <div className={styles.body}>
      <Typography size="small">Marketplace channels by brand uptake</Typography>

      {addons.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>
            <ChannelIcon />
          </span>
          <Typography colour="secondary">No marketplace channels are available.</Typography>
        </div>
      ) : (
        <div className={styles.scroll}>
          <RowCards
            rows={buildRows(brands, addons).map(({ addonId, addonName, count }) => ({
              id: addonId,
              leading: <Logo src={null} name={addonName} />,
              title: addonName,
              meta: [adoptionMeta(count)],
            }))}
          />
        </div>
      )}
    </div>
  </DetailsPanel>
);
